import React, { useEffect, useState } from "react";
import { useSEO } from "../hooks/useSEO";
import { useReveal } from "../hooks/useReveal";
import { api } from "../lib/api";

// 9 lundis avant la cérémonie du 14 novembre 2026
const REVEAL_DATES = [
  new Date("2026-09-14T20:00:00+02:00"),
  new Date("2026-09-21T20:00:00+02:00"),
  new Date("2026-09-28T20:00:00+02:00"),
  new Date("2026-10-05T20:00:00+02:00"),
  new Date("2026-10-12T20:00:00+02:00"),
  new Date("2026-10-19T20:00:00+02:00"),
  new Date("2026-10-26T20:00:00+02:00"),
  new Date("2026-11-02T20:00:00+01:00"),
  new Date("2026-11-09T20:00:00+01:00"),
];

function formatRevealDate(date) {
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" }).toUpperCase();
}

function useCountdown(target) {
  const [diff, setDiff] = useState(() => Math.max(0, target - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setDiff(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const total = Math.floor(diff / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { d, h, m, s, done: diff === 0 };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function SealCard({ index, revealDate }) {
  const [hovered, setHovered] = useState(false);
  const { d, h, m, s } = useCountdown(revealDate.getTime());
  const dateLabel = formatRevealDate(revealDate);
  const isVeryClose = d === 0; // moins d'un jour → afficher HH:MM:SS

  return (
    <div
      className="flex flex-col"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ cursor: "default" }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          aspectRatio: "3 / 4",
          background: "#070706",
          border: `1px solid ${hovered ? "rgba(201,169,97,0.4)" : "#1a1a1a"}`,
          transition: "border-color 0.6s ease",
        }}
      >
        {/* Fond grain */}
        <div className="absolute inset-0 grain" style={{ opacity: 0.5 }} aria-hidden="true" />

        {/* Silhouette */}
        <div
          className="absolute"
          style={{
            bottom: 0, left: "50%", transform: "translateX(-50%)",
            width: "65%", height: "72%",
            background: "linear-gradient(180deg, transparent 0%, #0d0c0b 40%, #0d0c0b 100%)",
            borderRadius: "38% 38% 0 0",
            opacity: hovered ? 0.55 : 0.3,
            transition: "opacity 0.6s ease",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute"
          style={{
            top: "13%", left: "50%", transform: "translateX(-50%)",
            width: "26%", aspectRatio: "1",
            background: "#0d0c0b", borderRadius: "50%",
            opacity: hovered ? 0.55 : 0.3,
            transition: "opacity 0.6s ease",
          }}
          aria-hidden="true"
        />

        {/* Hairline or dorée */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{
            height: 1,
            background: `linear-gradient(90deg, transparent, rgba(201,169,97,${hovered ? 0.6 : 0.18}), transparent)`,
            transition: "background 0.6s ease",
          }}
          aria-hidden="true"
        />

        {/* Contenu centré */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-4"
          style={{ background: `rgba(10,9,8,${hovered ? 0.7 : 0.5})`, transition: "background 0.6s ease" }}
        >
          {/* Tampon CONFIDENTIEL */}
          <div
            style={{
              border: `1px solid rgba(201,169,97,${hovered ? 0.65 : 0.25})`,
              padding: "5px 12px",
              marginBottom: 18,
              transition: "border-color 0.6s ease",
            }}
          >
            <p className="label" style={{ fontSize: 8, letterSpacing: "0.35em", color: `rgba(201,169,97,${hovered ? 0.9 : 0.45})`, transition: "color 0.6s ease" }}>
              CONFIDENTIEL
            </p>
          </div>

          <p className="label" style={{ fontSize: 9, letterSpacing: "0.2em", color: "rgba(245,241,232,0.25)", marginBottom: 14 }}>
            MEMBRE N°{String(index + 1).padStart(2, "0")}
          </p>

          {/* Date de révélation */}
          <p
            className="font-serif"
            style={{
              fontSize: "clamp(13px, 1.4vw, 17px)",
              color: `rgba(201,169,97,${hovered ? 1 : 0.65})`,
              letterSpacing: "0.04em",
              textAlign: "center",
              lineHeight: 1.35,
              marginBottom: 16,
              transition: "color 0.6s ease",
            }}
          >
            {dateLabel}
          </p>

          {/* Countdown mini */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isVeryClose ? (
              <p className="label" style={{ fontSize: 10, color: "rgba(201,169,97,0.6)", letterSpacing: "0.1em" }}>
                {pad(h)}:{pad(m)}:{pad(s)}
              </p>
            ) : (
              <>
                <div style={{ textAlign: "center" }}>
                  <p className="font-serif" style={{ fontSize: 16, color: "rgba(201,169,97,0.55)", lineHeight: 1 }}>{pad(d)}</p>
                  <p className="label" style={{ fontSize: 7, color: "rgba(245,241,232,0.2)", letterSpacing: "0.15em", marginTop: 3 }}>J</p>
                </div>
                <p className="label" style={{ fontSize: 10, color: "rgba(201,169,97,0.25)", marginBottom: 6 }}>·</p>
                <div style={{ textAlign: "center" }}>
                  <p className="font-serif" style={{ fontSize: 16, color: "rgba(201,169,97,0.55)", lineHeight: 1 }}>{pad(h)}</p>
                  <p className="label" style={{ fontSize: 7, color: "rgba(245,241,232,0.2)", letterSpacing: "0.15em", marginTop: 3 }}>H</p>
                </div>
                <p className="label" style={{ fontSize: 10, color: "rgba(201,169,97,0.25)", marginBottom: 6 }}>·</p>
                <div style={{ textAlign: "center" }}>
                  <p className="font-serif" style={{ fontSize: 16, color: "rgba(201,169,97,0.55)", lineHeight: 1 }}>{pad(m)}</p>
                  <p className="label" style={{ fontSize: 7, color: "rgba(245,241,232,0.2)", letterSpacing: "0.15em", marginTop: 3 }}>MIN</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sous la carte */}
      <div className="mt-4 flex items-center gap-3">
        <div style={{ width: 24, height: 1, background: `rgba(201,169,97,${hovered ? 0.5 : 0.15})`, transition: "background 0.6s ease" }} aria-hidden="true" />
        <p className="label" style={{ fontSize: 9, letterSpacing: "0.2em", color: hovered ? "rgba(245,241,232,0.4)" : "rgba(245,241,232,0.15)", transition: "color 0.6s ease" }}>
          IDENTITÉ SCELLÉE
        </p>
      </div>
    </div>
  );
}

function JuryMemberCard({ member }) {
  return (
    <figure className="flex flex-col">
      <div className="relative overflow-hidden" style={{ aspectRatio: "3 / 4", background: "#111" }}>
        {member.portrait ? (
          <img
            src={member.portrait}
            alt={member.name}
            className="bw-image w-full h-full object-cover"
            loading="lazy"
            style={{ transition: "transform 0.8s ease" }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#0e0e0e" }} />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.55) 100%)" }} aria-hidden="true" />
      </div>
      <figcaption className="mt-6">
        <p className="font-serif" style={{ fontSize: 22, color: "#F8F6F0", lineHeight: 1.15 }}>{member.name}</p>
        <p className="label mt-2" style={{ color: "#A1A1AA" }}>{member.role}</p>
      </figcaption>
    </figure>
  );
}

function NextRevealCountdown() {
  const now = Date.now();
  const next = REVEAL_DATES.find((d) => d.getTime() > now);
  const { d, h, m, s } = useCountdown(next ? next.getTime() : 0);
  if (!next) return null;

  return (
    <div className="mt-14 flex flex-wrap gap-10 items-end">
      {[{ label: "JOURS", value: pad(d) }, { label: "HEURES", value: pad(h) }, { label: "MINUTES", value: pad(m) }, { label: "SECONDES", value: pad(s) }].map(({ label, value }) => (
        <div key={label}>
          <p className="font-serif" style={{ fontSize: "clamp(36px, 5vw, 72px)", color: "#C9A961", lineHeight: 1, letterSpacing: "-0.02em" }}>
            {value}
          </p>
          <p className="label mt-2" style={{ color: "rgba(245,241,232,0.3)", fontSize: 10 }}>{label}</p>
        </div>
      ))}
      <p className="font-serif italic self-end pb-2" style={{ fontSize: "clamp(15px, 1.5vw, 19px)", color: "rgba(245,241,232,0.35)", maxWidth: 260, lineHeight: 1.5 }}>
        avant la prochaine révélation
      </p>
    </div>
  );
}

export default function Jury() {
  useSEO("Le Jury", "Neuf membres révélés un par semaine. Le jury du Stand d'Or 2026 se dévoile progressivement jusqu'au 22 juin.");

  const [jury, setJury] = useState([]);
  const heroRef = useReveal();
  const gridRef = useReveal();

  const now = Date.now();
  const allRevealed = now >= REVEAL_DATES[REVEAL_DATES.length - 1].getTime();

  useEffect(() => {
    api.get("/jury").then(({ data }) => setJury(data || [])).catch(() => {});
  }, []);

  // Combien de slots sont passés
  const revealedCount = REVEAL_DATES.filter((d) => now >= d.getTime()).length;

  return (
    <div data-testid="jury-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="reveal relative overflow-hidden grain"
        style={{ minHeight: "55vh", background: "#000", display: "flex", alignItems: "flex-end" }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(201,169,97,0.05) 0%, rgba(0,0,0,0) 60%)" }} aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-[180px] pb-24 w-full">
          <p className="label mb-8" style={{ color: "#C9A961" }}>
            Le jury · Édition MMXXVI — {revealedCount}/{REVEAL_DATES.length} révélé{revealedCount > 1 ? "s" : ""}
          </p>
          <h1
            className="font-serif"
            style={{ fontSize: "clamp(52px, 10vw, 140px)", lineHeight: 0.9, letterSpacing: "-0.03em", color: "#F8F6F0" }}
          >
            {allRevealed ? "Le collège." : "Neuf voix."}
            <br />
            <em style={{ color: allRevealed ? "#C9A961" : "rgba(245,241,232,0.3)" }}>
              {allRevealed ? "Un verdict." : "Neuf semaines."}
            </em>
          </h1>

          {!allRevealed && <NextRevealCountdown />}
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* GRILLE */}
      <section
        ref={gridRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-5">
            <p className="label mb-6" style={{ color: "#C9A961" }}>
              {allRevealed ? "Le collège" : "Révélations progressives"}
            </p>
            <h2 className="font-serif" style={{ fontSize: "clamp(30px, 3.5vw, 52px)", lineHeight: 1.06, color: "#F8F6F0" }}>
              {allRevealed
                ? "Un collège indépendant, réuni à huis clos."
                : "Un membre dévoilé chaque lundi, pendant neuf semaines."}
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end" style={{ color: "rgba(248,246,240,0.62)", fontSize: 16, lineHeight: 1.75 }}>
            <p>
              {allRevealed
                ? "Neuf personnalités issues de la mise en scène, de la programmation, de la critique et de l'écriture. Une présidence rotative. Un vote à bulletin unique, délibéré à huis clos."
                : "Neuf personnalités issues de la mise en scène, de la programmation, de la critique et de l'écriture. Chaque identité reste confidentielle jusqu'à sa date de révélation officielle."}
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12"
          style={{ borderTop: "1px solid #1c1c1c", paddingTop: 48 }}
        >
          {REVEAL_DATES.map((revealDate, i) => {
            const isSlotRevealed = now >= revealDate.getTime();
            const member = jury[i];
            return isSlotRevealed && member
              ? <JuryMemberCard key={i} member={member} />
              : <SealCard key={i} index={i} revealDate={revealDate} />;
          })}
        </div>

        {!allRevealed && (
          <p
            className="font-serif italic mt-20"
            style={{ fontSize: "clamp(18px, 2vw, 28px)", color: "rgba(245,241,232,0.2)", maxWidth: 560, lineHeight: 1.5 }}
          >
            « Le jury délibère dans l'ombre avant de rendre son verdict à la lumière. »
          </p>
        )}
      </section>
    </div>
  );
}
